import { IconSearch, IconShield, IconX } from "@tabler/icons-react";
import AppDialog from "components/common/AppDialog";
import { AdvancedConfirmDialog, NoData, Pagination, StyledTableHead, TableContainer } from "components";
import { LangConstant, SystemConstant } from "const";
import { useAccountList, useCreateChannel, useUnitList, useDepartmentList } from "hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonUtils } from "utils";

const CreateChannelDialog = ({ open, groupType, onClose }) => {
  const { t: getLabel } = useTranslation();
  const labels = getLabel(LangConstant.OBJ_CREATE_CHANNEL_DIALOG, { returnObjects: true });

  const isChannel = true;

  const { totalUnitList } = useUnitList();
  const { departmentList, handleToggleShowDepartment } = useDepartmentList();
  const [filterDepartmentList, setFilterDepartmentList] = useState([]);

  const {
    accounts,
    totalPages,
    filters,
    isFetching,
    handleSearch,
    handlePageChange,
    handleFilterUnit,
    handleFilterDepartment,
    handleResetQuery,
  } = useAccountList({
    groupId: "new-channel",
  });
  const { createChannel, isLoading: isCreating } = useCreateChannel();

  const [channelName, setChannelName] = useState("");
  const [privateF, setPrivateF] = useState(SystemConstant.CHANNEL_MODE.private.code);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [adminIds, setAdminIds] = useState([]);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [errors, setErrors] = useState({});

  const selectedIds = useMemo(() => selectedUsers.map(u => u.id), [selectedUsers]);

  const columns = [
    { label: "", align: "center", width: "35px" },
    { label: getLabel("TXT_FULL_NAME") || "HỌ VÀ TÊN", align: "left", width: "25%" },
    { label: getLabel("TXT_UNIT") || "ĐƠN VỊ", align: "left", width: "25%" },
    { label: getLabel("TXT_DEPARTMENT") || "PHÒNG BAN", align: "left", width: "25%" },
    { label: getLabel("TXT_POSITION") || "VỊ TRÍ", align: "left", width: "25%" },
  ];

  useEffect(() => {
    const selectedUnitId = filters?.unitId;
    if (selectedUnitId && departmentList && Object.keys(departmentList).includes(String(selectedUnitId))) {
      setFilterDepartmentList(
        [{ departmentId: 0, departmentName: getLabel("TXT_FILTER_ALL") || "Tất cả phòng ban" }].concat(
          [...departmentList[selectedUnitId]].sort((a, b) => {
            return (a.departmentName || "").localeCompare(b.departmentName || "", "vi");
          }),
        ),
      );
    } else {
      setFilterDepartmentList([{ departmentId: 0, departmentName: getLabel("TXT_FILTER_ALL") || "Tất cả phòng ban" }]);
    }
  }, [departmentList, filters?.unitId, getLabel]);

  const onChangeUnit = e => {
    const unitId = e.target.value;
    handleFilterUnit(unitId);
    handleToggleShowDepartment(unitId);
    setCurrentPage(1);
  };

  const onChangeDepartment = e => {
    const deptId = e.target.value;
    handleFilterDepartment(deptId);
    setCurrentPage(1);
  };

  const handleSelectUser = useCallback(
    user => {
      const uid = user.accountId || user.id;
      setSelectedUsers(prev => {
        const exists = prev.find(u => u.id === uid);
        if (exists) {
          const updated = prev.filter(u => u.id !== uid);
          // If removing the admin, clear from adminIds
          setAdminIds(prevAdminIds => prevAdminIds.filter(id => id !== uid));
          return updated;
        }
        return [...prev, { id: uid, name: user.name, unitName: user.unitName, departmentName: user.departmentName, positionName: user.positionName }];
      });
      if (errors.members) setErrors(prev => ({ ...prev, members: null }));
    },
    [errors.members],
  );

  const handleRemoveUser = useCallback(
    userId => {
      setSelectedUsers(prev => prev.filter(u => u.id !== userId));
      setAdminIds(prev => prev.filter(id => id !== userId));
    },
    [],
  );

  const handleToggleAdmin = useCallback(
    userId => {
      setAdminIds(prev => {
        const exists = prev.includes(userId);
        if (exists) {
          return prev.filter(id => id !== userId);
        }
        return [...prev, userId];
      });
      if (errors.admin) setErrors(prev => ({ ...prev, admin: null }));
    },
    [errors.admin],
  );

  const handlePageClick = (e, page) => {
    setCurrentPage(page);
    handlePageChange(page);
  };

  const handleSearchChange = e => {
    handleSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleChangeName = e => {
    setChannelName(e.target.value);
    if (errors.name) setErrors(prev => ({ ...prev, name: null }));
  };

  const handleChangeModeSelect = e => {
    setPrivateF(parseInt(e.target.value));
  };

  const validate = () => {
    const newErrors = {};
    if (!channelName.trim()) {
      newErrors.name = labels.nameRequired;
    }
    if (selectedUsers.length < 3) {
      newErrors.members = "Vui lòng chọn ít nhất 3 thành viên";
    }
    if (adminIds.length === 0) {
      newErrors.admin = labels.adminRequired;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenConfirm = () => {
    if (!validate()) return;
    setIsOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setIsOpenConfirm(false);
  };

  const handleConfirmCreate = () => {
    const groupTypeValue = isChannel ? SystemConstant.GROUP_TYPE.CHANNEL : SystemConstant.GROUP_TYPE.GROUP;

    const members = [...selectedIds];
    adminIds.forEach(id => {
      if (id && !members.includes(id)) {
        members.push(id);
      }
    });

    const payload = {
      groupId: CommonUtils.uuid(),
      groupType: groupTypeValue,
      members,
      groupName: JSON.stringify({ name: channelName.trim() }),
      privateF,
      encryptionF: 1,
      adminId: adminIds,
      accountId: adminIds[0] || "",
      onSuccess: () => {
        handleClose();
      },
    };

    createChannel(payload);
    setIsOpenConfirm(false);
  };

  const handleClose = () => {
    setChannelName("");
    setPrivateF(SystemConstant.CHANNEL_MODE.private.code);
    setSelectedUsers([]);
    setAdminIds([]);
    setErrors({});
    setCurrentPage(1);
    handleResetQuery();
    onClose();
  };

  if (!open) return null;

  return (
    <AppDialog
      open={open}
      title={isChannel ? labels.title : labels.titleGroup}
      onClose={handleClose}
      style={{ width: 1000, maxWidth: "95vw" }}
    >
      <div className="d-flex flex-column gap-3" style={{ marginTop: 8 }}>
        {/* Row 1: Channel name + Mode on the same line */}
        <div className="d-flex gap-3">
          <div style={{ flex: 2 }}>
            <label className="form-label" style={{ fontWeight: 600 }}>
              {isChannel ? labels.channelName : labels.groupName} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              placeholder={isChannel ? labels.channelNamePlaceholder : labels.groupNamePlaceholder}
              value={channelName}
              onChange={handleChangeName}
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>
          <div style={{ flex: 1 }}>
            <label className="form-label" style={{ fontWeight: 600 }}>
              {labels.channelMode}
            </label>
            <select className="form-select" value={privateF} onChange={handleChangeModeSelect}>
              {Object.values(SystemConstant.CHANNEL_MODE).map((item, index) => (
                <option key={index} value={item.code}>
                  {getLabel(item.label)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2: Admin selection */}
        <div>
          <label className="form-label" style={{ fontWeight: 600 }}>
            {isChannel ? labels.channelAdmin : labels.groupAdmin} <span className="text-danger">*</span>
          </label>
          <div
            className={`form-control d-flex flex-wrap align-items-center gap-1.5 ${errors.admin ? "is-invalid" : ""}`}
            style={{
              minHeight: "38px",
              maxHeight: "68px",
              overflowY: "auto",
              padding: "4px 8px",
            }}
          >
            {adminIds.length === 0 ? (
              <span className="text-muted" style={{ fontSize: 13 }}>
                Vui lòng chọn ít nhất 1 quản trị viên (tích chọn biểu tượng Quản trị 🛡️ ở cột bên phải)
              </span>
            ) : (
              selectedUsers
                .filter(u => adminIds.includes(u.id))
                .map(user => (
                  <span
                    key={user.id}
                    className="badge bg-blue-lt d-inline-flex align-items-center gap-1"
                    style={{ padding: "4px 8px", fontSize: 13, fontWeight: 500 }}
                  >
                    <IconShield size={14} style={{ color: "#206bc4" }} />
                    {user.name}
                    <IconX
                      size={13}
                      className="cursor-pointer ms-1"
                      style={{ opacity: 0.7 }}
                      onClick={() => handleToggleAdmin(user.id)}
                    />
                  </span>
                ))
            )}
          </div>
          {errors.admin && <div className="invalid-feedback">{errors.admin}</div>}
        </div>

        {/* Row 3: Two-panel layout - member search (left) + selected list (right) */}
        <div className="d-flex gap-3" style={{ minHeight: 320 }}>
          {/* Left panel: search & select members */}
          <div style={{ flex: 2, display: "flex", flexDirection: "column" }}>
            <label className="form-label" style={{ fontWeight: 600 }}>
              {labels.selectMembers}
            </label>
            <div className="d-flex gap-2 mb-2">
              <div className="input-group" style={{ flex: 1 }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={labels.searchPlaceholder}
                  onChange={handleSearchChange}
                />
                <span className="input-group-text">
                  <IconSearch size={16} />
                </span>
              </div>

              <select
                className="form-select"
                style={{ width: "auto", minWidth: 120, maxWidth: 160 }}
                value={filters?.unitId || 0}
                onChange={onChangeUnit}
              >
                <option value="0">{getLabel("TXT_FILTER_ALL") || "Tất cả đơn vị"}</option>
                {totalUnitList.map(unit => (
                  <option key={unit.unitId} value={unit.unitId}>
                    {unit.unitName}
                  </option>
                ))}
              </select>

              <select
                className="form-select"
                style={{ width: "auto", minWidth: 120, maxWidth: 160 }}
                value={filters?.departmentId || 0}
                onChange={onChangeDepartment}
              >
                <option value="0">{getLabel("TXT_FILTER_ALL") || "Tất cả phòng ban"}</option>
                {filterDepartmentList.map(dept => (
                  <option key={dept.departmentId} value={dept.departmentId}>
                    {dept.departmentName}
                  </option>
                ))}
              </select>
            </div>

            <TableContainer style={{ flex: 1, maxHeight: 260, overflowY: "auto" }}>
              <table className="table table-vcenter table-hover">
                <StyledTableHead columns={columns} />
                <tbody>
                  {accounts.map(user => {
                    const uid = user.accountId || user.id;
                    const isChecked = selectedIds.includes(uid);
                    return (
                      <tr
                        key={uid}
                        onClick={() => handleSelectUser(user)}
                        style={{ cursor: "pointer" }}
                        role="checkbox"
                        aria-checked={isChecked}
                      >
                        <td className="text-center">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isChecked}
                            readOnly
                          />
                        </td>
                        <td className="text-left">{user.name}</td>
                        <td className="text-left">{user.unitName || "--"}</td>
                        <td className="text-left">{user.departmentName || "--"}</td>
                        <td className="text-left">{user.positionName || "--"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {accounts.length === 0 && !isFetching && <NoData />}
            </TableContainer>

            {totalPages > 1 && (
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageClick} />
            )}
          </div>

          {/* Right panel: selected members list */}
          <div style={{ width: 220, minWidth: 220, display: "flex", flexDirection: "column" }}>
            <label className="form-label" style={{ fontWeight: 600 }}>
              {labels.selectedMembers} ({selectedUsers.length})
            </label>
            {errors.members && <small className="text-danger d-block mb-1">{errors.members}</small>}

            <div
              style={{
                flex: 1,
                maxHeight: 300,
                overflowY: "auto",
                border: "1px solid var(--tblr-border-color, rgba(128, 128, 128, 0.2))",
                borderRadius: 4,
              }}
            >
              {selectedUsers.length === 0 ? (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted text-center" style={{ padding: 16, fontSize: 13 }}>
                  Vui lòng chọn ít nhất 3 thành viên
                </div>
              ) : (
                <div className="d-flex flex-column">
                  {selectedUsers.map(user => {
                    const isAdmin = adminIds.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        className="d-flex align-items-center justify-content-between"
                        style={{
                          padding: "6px 10px",
                          backgroundColor: isAdmin ? "rgba(32, 107, 196, 0.08)" : "transparent",
                        }}
                      >
                        <div className="d-flex align-items-center gap-1" style={{ minWidth: 0, flex: 1 }}>
                          {isAdmin && <IconShield size={14} style={{ color: "#206bc4", flexShrink: 0 }} />}
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: isAdmin ? 600 : 400,
                              color: isAdmin ? "#206bc4" : "inherit",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {user.name}
                          </span>
                        </div>
                        <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0 }}>
                          <button
                            type="button"
                            className={`btn btn-icon ${isAdmin ? "btn-primary" : "btn-ghost-secondary"}`}
                            style={{ width: 22, height: 22, padding: 0 }}
                            title={isAdmin ? labels.removeAdmin || "Gỡ quản trị viên" : labels.setAdmin || "Gán làm quản trị viên"}
                            onClick={() => handleToggleAdmin(user.id)}
                          >
                            <IconShield size={13} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-icon btn-ghost-secondary"
                            style={{ width: 22, height: 22, padding: 0 }}
                            title={labels.remove}
                            onClick={() => handleRemoveUser(user.id)}
                          >
                            <IconX size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <button className="btn btn-link link-secondary" onClick={handleClose}>
          {getLabel(LangConstant.L_CANCEL)}
        </button>
        <button
          className="btn btn-primary"
          disabled={isCreating || selectedUsers.length < 3}
          onClick={handleOpenConfirm}
        >
          {isChannel ? labels.submitChannel || "Tạo kênh" : labels.submitGroup || "Tạo nhóm"}
        </button>
      </div>

      {isOpenConfirm && (
        <AdvancedConfirmDialog
          open
          title={isChannel ? labels.title : labels.titleGroup}
          content={labels.confirm}
          submitProps={{ submitText: isChannel ? labels.submitChannel || "Tạo kênh" : labels.submitGroup || "Tạo nhóm", onClick: handleConfirmCreate }}
          cancelProps={{ onClick: handleCloseConfirm }}
        />
      )}
    </AppDialog>
  );
};

export default CreateChannelDialog;
